type Observer<TSubject> = (subject: TSubject) => void;

type Subject<TSubject> = {
  readonly subscribe: (observer: Observer<TSubject>) => () => void;
  readonly unsubscribe: (observer: Observer<TSubject>) => void;
  readonly publish: (messageSubject: TSubject) => void;
};

/**
 * Creates a subject for implementing the Observer pattern.
 *
 * @returns An object containing three functions:
 * - subscribe: A function to add an observer to the subject.
 * - unsubscribe: A function to remove an observer from the subject.
 * - publish: A function to notify all observers of an event.
 */
export function subject<TSubject = void>(): Subject<TSubject> {
  let observers: Observer<TSubject>[] = [];

  /**
   * Removes an observer from the subject.
   *
   * @param observer - The observer to remove.
   */
  const unsubscribe = (observer: Observer<TSubject>): void => {
    observers = observers.filter((observed) => observed !== observer);
  };

  /**
   * Adds an observer to the subject.
   *
   * @param observer - The observer to add.
   * @returns A function that removes the observer from the subject.
   */
  const subscribe = (observer: Observer<TSubject>) => {
    observers.push(observer);

    return (): void => {
      unsubscribe(observer);
    };
  };

  /**
   * Notifies all observers of an event.
   *
   * @param messageSubject - The subject to pass to the observers.
   */
  const publish = (messageSubject: TSubject): void => {
    for (const observer of observers) {
      observer(messageSubject);
    }
  };

  return {
    subscribe,
    unsubscribe,
    publish,
  } as const;
}
